import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHref, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { StageActionDefinition, StageIntent, TargetResolver, TransitionDefinition } from 'types/arc';
import type { FilterGroup, UnifiedFilters } from 'types/filters';
import type { Schema } from 'types/schema';
import type { AppDispatch } from '@center-pwa/store/store';
import { saveArcTemplate, publishArcTemplate } from '@center-pwa/reducers/arcTemplatesReducer';
import { fetchContexts, selectContextsList } from '@center-pwa/reducers/contextsReducer';
import {
  useApiClientsOrNull,
  useArcTemplateEditor,
  useArcEditorCanvasState,
  useOrgAvailableTypes,
  useStudioTemplates,
  EditorRailShellOrganism,
  ArcEditorCanvasOrganism,
  ArcStageCardMolecule,
  ArcActionCardMolecule,
  ArcIntentRowMolecule,
  ArcLaneLabelMolecule,
  EditorHeaderMolecule,
  GlyphAtom,
  MessageAtom,
  PrimeReactButton as ButtonAtom,
  TagAtom,
  intentsInLane,
  distinctIntentLanes,
  resolveLanesForModel,
  intentCountByLane,
  unattributedIntentCount,
  UNATTRIBUTED_LANE_KEY,
  actionSubjectOf,
  actionTierOf,
  actionTierHoldsForPerson,
  modelToDefinition,
  structuralChecks,
  deriveEdges,
  isWholeDayOffset,
  parseOffset,
  transitionRemoveKeepsOtherExits,
  keyDateRowsOf,
  arcSettingsOf,
  confirmDialogAtom,
  ARC_CANVAS_STARTERS,
  buildFilterPropertyOptions,
  deriveApplicability,
  buildApplicability,
  earlierActionsInHook,
  filterStateToUnified,
  unifiedToFilterState,
  getLocalizedText,
  type ArcEditorCanvasView,
  type ArcEditorCanvasSurfaceTag,
  type ArcActionCardAction,
  type ArcCanvasAuthoredActionType,
  type ArcCanvasAuthoredTargetKind,
  type ArcIntentRowIntent,
  type GateCheck,
  type GateCheckSelection,
  type BlockPaletteItem,
  type BlockPlacementTarget,
  type ArcFlowViewNode,
  type ArcFlowViewTransition,
  type SchemaForFilters,
  type ApplicabilityMode,
  type FilterState,
} from 'design-system/my-lib';

import './ArcEditorPage.scss';

/** A stable reference (see `initialApplicability` below — reusing the same fallback object on
 * every render, instead of a fresh literal, avoids a redundant extra render on mount). */
const EMPTY_APPLICABILITY_FILTER: FilterState = { propertyFilters: [], perspectiveFilters: [] };

/** `AC_LIBRARY`/`AC_LIB_GROUPS` (`arc-data.jsx:627-639`) — the palette's static block catalog. */
const ARC_PALETTE_ITEMS = [
  { id: 'stage-plain', group: 'groupStages', glyph: 'lucide:square-dashed', nameKey: 'stagePlainName', noteKey: 'stagePlainNote' },
  { id: 'stage-terminal', group: 'groupStages', glyph: 'lucide:flag', nameKey: 'stageTerminalName', noteKey: 'stageTerminalNote' },
  { id: 'intent-plain', group: 'groupIntents', glyph: 'lucide:circle', nameKey: 'intentPlainName', noteKey: 'intentPlainNote' },
  { id: 'intent-decision', group: 'groupIntents', glyph: 'lucide:gavel', nameKey: 'intentDecisionName', noteKey: 'intentDecisionNote' },
  { id: 'intent-review', group: 'groupIntents', glyph: 'lucide:eye', nameKey: 'intentReviewName', noteKey: 'intentReviewNote' },
  { id: 'act-attach', group: 'groupActions', glyph: 'lucide:tag', nameKey: 'actAttachName', noteKey: 'actAttachNote', actionType: 'attach_aspect' },
  { id: 'act-set', group: 'groupActions', glyph: 'lucide:pen-line', nameKey: 'actSetName', noteKey: 'actSetNote', actionType: 'set_aspect_value' },
  { id: 'act-deploy', group: 'groupActions', glyph: 'lucide:package', nameKey: 'actDeployName', noteKey: 'actDeployNote', actionType: 'deploy_approach' },
  { id: 'act-factor', group: 'groupActions', glyph: 'lucide:activity', nameKey: 'actFactorName', noteKey: 'actFactorNote', actionType: 'attach_status_factor' },
] as const;
const ARC_PALETTE_GROUP_ORDER = ['groupStages', 'groupIntents', 'groupActions'] as const;
type ArcPaletteGroup = (typeof ARC_PALETTE_GROUP_ORDER)[number];

/** `arc_editor_block_added.block` (spec § Analytics) names the kind of block, not the tile. */
const ANALYTICS_BLOCK_OF_GROUP: Record<ArcPaletteGroup, 'stage' | 'intent' | 'action'> = {
  groupStages: 'stage',
  groupIntents: 'intent',
  groupActions: 'action',
};

/** `structuralChecks`' own `id`s (`arcCanvasModel.ts`) mapped to their `footer.gate.checks.*`
 * i18n key — the util returns developer-composed English messages (`detail`), so the chip's
 * `label` is always the localized one, never the raw sentence. */
const CHECK_LABEL_KEY: Record<string, string> = {
  'no-terminal-stage': 'noTerminalStage',
  'multiple-terminal-stages': 'multipleTerminalStages',
  'auto-transition-no-target': 'autoTransitionNoTarget',
  'auto-transition-multiple-targets': 'autoTransitionMultipleTargets',
  'undeclared-key-date-anchor': 'undeclaredKeyDateAnchor',
  'calculated-transition-missing-target': 'calculatedTransitionMissingTarget',
  'calculated-transition-missing-condition': 'calculatedTransitionMissingCondition',
  'dangling-transition-target': 'danglingTransitionTarget',
  'empty-aspect-reference': 'emptyAspectReference',
  'empty-approach-reference': 'emptyApproachReference',
  'empty-target-reference': 'emptyTargetReference',
  'unreachable-stage': 'unreachableStage',
};

/** The gate bar routes a chip by an opaque `sectionId`; this is the id the page mints for a
 * check's selection so the activation can be routed back through `selectStage`/`selectAction`
 * by kind (an action-level check selects the action, not its stage). */
function sectionIdOf(selection: GateCheckSelection): string {
  return selection.kind === 'action'
    ? `action:${selection.stageId}:${selection.actionId}`
    : `stage:${selection.stageId}`;
}

/** Transition-kind glyphs for the flow view's edges (spec "Renders as" note — `GlyphAtom` with
 * `lucide:<name>`, never a PrimeIcon class). */
const TRANSITION_KIND_GLYPH: Record<'manual' | 'auto' | 'calculated', string> = {
  manual: 'hand',
  auto: 'calendar-clock',
  calculated: 'sigma',
};

/** The flow view's edge id: the source stage plus the `AvailableAction` the edge is drawn from
 * (`calculated` for a calculated exit, which has none). Shared by the edge list and the
 * transition selection so both name the same edge. */
function flowEdgeId(stageId: string, actionId: string | null): string {
  return `${stageId}:${actionId ?? 'calculated'}`;
}

type Translate = ReturnType<typeof useTranslation>['t'];

/** How an exit moves — the second sentence of the transition inspector's read-back
 * (`acTransSentence`). An auto exit reads as "{on | n days after | n days before} {the <key date>
 * date | the sphere's <property> date}" (`acOffsetPhrase` / `acAnchorLabel`): an offset the engine
 * cannot parse as whole days reads as "on", the way the engine will fire it. */
function transitionSummaryTail(
  t: Translate,
  ns: string,
  stageName: string,
  transition: TransitionDefinition,
  anchorLabel: string | undefined
): string {
  if (transition.type === 'calculated') {
    const conditions = transition.calculatedConditions;
    return conditions?.expressions?.length || conditions?.groups?.length
      ? t(`${ns}.summaryCalculated`)
      : t(`${ns}.summaryCalculatedNoCondition`);
  }
  if (transition.type !== 'auto') return t(`${ns}.summaryManual`, { from: stageName });

  const anchor = transition.dateOffsetAnchor ?? '';
  if (!anchor) return t(`${ns}.summaryAutoNoAnchor`);
  const source = transition.anchorSource ?? 'keyDate';
  const anchorPhrase = anchorLabel
    ? t(`${ns}.${source === 'property' ? 'anchorProperty' : 'anchorKeyDate'}`, { label: anchorLabel })
    : t(`${ns}.${source === 'property' ? 'anchorPropertyMissing' : 'anchorKeyDateMissing'}`);
  const offset = transition.dateOffset?.trim() ?? '';
  const days = offset && isWholeDayOffset(offset) ? parseOffset(offset).value : 0;
  let when: string;
  if (days === 0) when = t(`${ns}.offsetOn`);
  else if (days > 0) when = t(`${ns}.offsetAfter`, { count: days });
  else when = t(`${ns}.offsetBefore`, { count: -days });
  return t(`${ns}.summaryAuto`, { when, anchor: anchorPhrase });
}

/** Each droppable action type's verb chip reuses its palette tile's name (`arc-data.jsx`'s
 * `AC_ACTION_TYPES[*].label`, lowercased on the card — `arc-lane.jsx:96`). */
const ACTION_TYPE_VERB_KEY: Record<string, string> = {
  attach_aspect: 'studio.arcEditor.palette.library.actAttachName',
  set_aspect_value: 'studio.arcEditor.palette.library.actSetName',
  deploy_approach: 'studio.arcEditor.palette.library.actDeployName',
  attach_status_factor: 'studio.arcEditor.palette.library.actFactorName',
};

/** The action types the inspector authors in full (spec Data Model "Action"); the factor kinds
 * are offered only as an action's current type. */
const AUTHORED_ACTION_TYPES: ArcCanvasAuthoredActionType[] = [
  'attach_aspect',
  'detach_aspect',
  'set_aspect_value',
  'deploy_approach',
];
const AUTHORED_TARGET_KINDS: ArcCanvasAuthoredTargetKind[] = [
  'self',
  'parent',
  'descendant',
  'context_member',
  'created_by_action',
];
const isAuthoredActionType = (type: string): type is ArcCanvasAuthoredActionType =>
  (AUTHORED_ACTION_TYPES as string[]).includes(type);
const isAuthoredTargetKind = (kind: string): kind is ArcCanvasAuthoredTargetKind =>
  (AUTHORED_TARGET_KINDS as string[]).includes(kind);

/** The catalogs the action inspector's reference picker and the stage inspector's requirement
 * picker read: every aspect and approach the caller may see, from the same permission-honest
 * gallery route the Studio galleries use. */
const REFERENCE_CATALOG_TYPES = ['aspect', 'approach'];

/**
 * `/studio/arc/new` and `/studio/arc/:systemName/:version` — the Arc Editor Canvas
 * (EAINT-951). A thin route host, like `SphereTypeEditorPage`: `useArcTemplateEditor` owns the
 * fetch + version resolution + fail-closed `locked` gate, `useArcEditorCanvasState` owns the
 * draft model and the selection, and this page maps both onto `ArcEditorCanvasOrganism`'s
 * regions (stage grid, flow view, inspector rail, palette, publish gate) plus the row-level
 * `applicability`, Save (`PUT` the definition) and Publish (save, then `PUT isPublished`).
 */
const ArcEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const {
    schemas: orgAvailableSchemas,
    sources: orgAvailableSources,
    approachSources: orgApproachSources,
  } = useOrgAvailableTypes(apiClients);
  const {
    templates: referenceCatalog,
    error: referenceCatalogError,
  } = useStudioTemplates(apiClients, { types: REFERENCE_CATALOG_TYPES });
  const aspectOptions = useMemo(
    () =>
      referenceCatalog
        .filter((card) => card.type === 'aspect')
        .map((card) => ({ value: card.systemName, label: card.displayName })),
    [referenceCatalog]
  );
  const approachOptions = useMemo(
    () =>
      referenceCatalog
        .filter((card) => card.type === 'approach')
        .map((card) => ({ value: card.systemName, label: card.displayName })),
    [referenceCatalog]
  );
  /** Display name for a referenced aspect/approach `systemName`; the system name itself when the
   * catalog does not list it (not loaded yet, or a reference the caller cannot see). */
  const referenceLabel = useCallback(
    (reference: string | undefined) =>
      reference
        ? referenceCatalog.find((card) => card.systemName === reference)?.displayName ?? reference
        : undefined,
    [referenceCatalog]
  );
  // The `context_member` target kind's context picker reads the same org-wide contexts list
  // Studio's own context gallery reads (`ContextListPage`'s `fetchContexts`/`selectContextsList`)
  // — an arc template has no sphere to scope `useAvailableContexts` against.
  const contextsList = useSelector(selectContextsList);
  useEffect(() => {
    if (!apiClients?.schemaClient) return;
    dispatch(fetchContexts({ apiClient: apiClients.schemaClient }));
  }, [apiClients, dispatch]);
  const contextOptions = useMemo(
    () => contextsList.map((context) => ({ value: context.system_name, label: getLocalizedText(context.name, context.system_name) })),
    [contextsList]
  );

  const { systemName, version: versionParam } = useParams<{ systemName?: string; version?: string }>();
  const version = versionParam != null ? Number(versionParam) : undefined;

  const { status, row, locked, isSystem, retry, replaceRow } = useArcTemplateEditor(systemName, version);
  const editable = status === 'ready' ? !locked : status === 'new';
  const definition = row?.definition ?? null;
  const {
    model,
    dirty,
    unsavedChangeCount,
    selectedArc,
    selectArc,
    updateArcMeta,
    updateArcSettings,
    addKeyDate,
    updateKeyDate,
    removeKeyDate,
    selectedStageId,
    selectStage,
    selectedTransition,
    selectTransition,
    updateTransition,
    removeTransition,
    selectedIntent,
    selectIntent,
    updateIntent,
    removeIntent,
    selectedAction,
    selectAction,
    updateAction,
    removeAction,
    renameStage,
    updateStage,
    removeStage,
    addStageRequirement,
    updateStageRequirement,
    removeStageRequirement,
    addStage,
    addIntent,
    addAction,
    addTransitionTarget,
    addStarterStages,
    summaryCounts,
    discard,
  } = useArcEditorCanvasState(definition);

  const [view, setView] = useState<ArcEditorCanvasView>('stages');
  const [paletteQuery, setPaletteQuery] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  // The arc's `applicability` (D24) — lives on the row, not `definition`, so it is not part of
  // `useArcEditorCanvasState`'s model; edited independently and reset the same way `model` is
  // whenever the loaded row's own applicability changes. Defaulted fields are computed once here
  // (not separately in the `useState` initializer and the sync effect below) so the effect's
  // first run — which always fires once on mount — hands back the SAME array/object references
  // the initializer already used, and React bails out of the otherwise-inevitable extra render.
  const initialApplicability = useMemo(() => {
    const derived = deriveApplicability(row?.applicability as UnifiedFilters | undefined);
    return {
      mode: derived.applicabilityMode ?? 'any',
      families: derived.applicabilityFamilies ?? [],
      types: derived.applicabilityTypes ?? [],
      filter: derived.applicabilityFilter ?? EMPTY_APPLICABILITY_FILTER,
    };
  }, [row?.applicability]);
  const [applicabilityMode, setApplicabilityMode] = useState<ApplicabilityMode>(initialApplicability.mode);
  const [applicabilityFamilies, setApplicabilityFamilies] = useState<string[]>(initialApplicability.families);
  const [applicabilityTypes, setApplicabilityTypes] = useState<string[]>(initialApplicability.types);
  const [applicabilityFilter, setApplicabilityFilter] = useState<FilterState>(initialApplicability.filter);
  useEffect(() => {
    setApplicabilityMode(initialApplicability.mode);
    setApplicabilityFamilies(initialApplicability.families);
    setApplicabilityTypes(initialApplicability.types);
    setApplicabilityFilter(initialApplicability.filter);
  }, [initialApplicability]);

  const availableProperties = useMemo(
    () => buildFilterPropertyOptions({ schemas: orgAvailableSchemas as unknown as SchemaForFilters[], t }),
    [orgAvailableSchemas, t]
  );

  /** `PUT`s the draft and swaps the returned row in as the saved baseline (`dirty` clears, the
   * surface tag reflects the row). Resolves to the saved row, or `null` when the save failed —
   * the failure is already surfaced in the banner, so callers only need to know not to go on. */
  const handleSave = useCallback(async () => {
    if (!apiClients?.schemaClient || !model || !row) return null;
    setSaving(true);
    setSaveErrorMessage(null);
    try {
      const saved = await dispatch(
        saveArcTemplate({
          apiClient: apiClients.schemaClient,
          systemName: row.system_name,
          version: row.version,
          payload: {
            definition: modelToDefinition(model),
            applicability: buildApplicability({
              applicabilityMode,
              applicabilityFamilies,
              applicabilityTypes,
              applicabilityFilter,
            }),
          },
        })
      ).unwrap();
      replaceRow(saved);
      return saved;
    } catch (err: unknown) {
      // The raw message is developer-facing English (axios/network text) — it is logged, never
      // rendered. The user sees the localized failure string.
      console.error('[ArcEditorPage] Failed to save arc template', err);
      setSaveErrorMessage(t('studio.arcEditor.saveFailed'));
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    apiClients,
    model,
    row,
    dispatch,
    replaceRow,
    t,
    applicabilityMode,
    applicabilityFamilies,
    applicabilityTypes,
    applicabilityFilter,
  ]);

  const handleDiscard = useCallback(() => {
    setSaveErrorMessage(null);
    discard();
    setApplicabilityMode(initialApplicability.mode);
    setApplicabilityFamilies(initialApplicability.families);
    setApplicabilityTypes(initialApplicability.types);
    setApplicabilityFilter(initialApplicability.filter);
  }, [discard, initialApplicability]);

  const modelChecks = useMemo(() => (model ? structuralChecks(model) : []), [model]);
  const footerChecks: GateCheck[] = useMemo(
    () =>
      modelChecks.map((check) => ({
        id: check.id,
        label: t(`studio.arcEditor.footer.gate.checks.${CHECK_LABEL_KEY[check.id] ?? check.id}`),
        state: 'open' as const,
        detail: check.message,
        sectionId: sectionIdOf(check.selection),
      })),
    [modelChecks, t]
  );
  const handleCheckActivate = useCallback(
    (sectionId: string) => {
      const selection = modelChecks.find((check) => sectionIdOf(check.selection) === sectionId)?.selection;
      if (!selection) return;
      if (selection.kind === 'action') {
        selectAction(selection.stageId, selection.actionId);
      } else {
        selectStage(selection.stageId);
      }
    },
    [modelChecks, selectAction, selectStage]
  );

  /** The gate is evaluated on the draft, so what gets published must be the draft: an unsaved
   * change is saved first, and a failed save stops the publish (the server would otherwise
   * publish the stale saved definition). */
  const handlePublish = useCallback(async () => {
    if (!apiClients?.schemaClient || !row) return;
    if (dirty && !(await handleSave())) return;
    setPublishing(true);
    setSaveErrorMessage(null);
    try {
      // `arc_template_published` fires from `storeAnalyticsMiddleware.ts` on this thunk's
      // `fulfilled` action — never duplicated here.
      const published = await dispatch(
        publishArcTemplate({
          apiClient: apiClients.schemaClient,
          systemName: row.system_name,
          version: row.version,
        })
      ).unwrap();
      replaceRow(published);
    } catch (err: unknown) {
      console.error('[ArcEditorPage] Failed to publish arc template', err);
      setSaveErrorMessage(t('studio.arcEditor.publishFailed'));
    } finally {
      setPublishing(false);
    }
  }, [apiClients, row, dirty, handleSave, dispatch, replaceRow, t]);

  /** Publish stays enabled while checks are open and explains on submit (ux-craft I2): a blocked
   * attempt fires `arc_editor_publish_blocked` and opens the first open check's editor, so the
   * author lands on what is holding the publish instead of on a dead button. */
  const handlePublishClick = useCallback(() => {
    if (!row) return;
    const openChecks = footerChecks.filter((check) => check.state === 'open');
    if (openChecks.length > 0) {
      logAnalyticsEvent('arc_editor_publish_blocked', undefined, {
        openChecks: openChecks.map((check) => check.id),
      });
      handleCheckActivate(openChecks[0].sectionId);
      return;
    }
    confirmDialogAtom({
      header: t('studio.arcEditor.footer.gate.publishConfirmTitle'),
      message: t(
        dirty
          ? 'studio.arcEditor.footer.gate.publishConfirmMessageUnsaved'
          : 'studio.arcEditor.footer.gate.publishConfirmMessage',
        { version: row.version }
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('studio.arcEditor.footer.gate.publishConfirmAccept'),
      rejectLabel: t('studio.arcEditor.footer.gate.publishConfirmCancel'),
      accept: () => {
        void handlePublish();
      },
    });
  }, [row, dirty, footerChecks, logAnalyticsEvent, handleCheckActivate, t, handlePublish]);

  const handleCellDrop = useCallback(
    (laneKey: string, stageId: string) => {
      const item = draggingId ? ARC_PALETTE_ITEMS.find((i) => i.id === draggingId) : undefined;
      setDraggingId(null);
      if (!item) return;
      logAnalyticsEvent('arc_editor_block_added', undefined, {
        block: ANALYTICS_BLOCK_OF_GROUP[item.group],
        via: 'drag',
      });
      if (item.group === 'groupStages') {
        addStage(stageId, t('studio.arcEditor.palette.newStageName'), { terminal: item.id === 'stage-terminal' });
      } else if (item.group === 'groupIntents') {
        addIntent(stageId, laneKey, t(`studio.arcEditor.palette.library.${item.nameKey}`));
      } else if (item.group === 'groupActions') {
        addAction(stageId, item.actionType);
      }
    },
    [draggingId, addStage, addIntent, addAction, t, logAnalyticsEvent]
  );

  /** Appends a stage after the last one — plain, or terminal for the palette's "Terminal stage"
   * tile — and fires the one `block_added` event every button path shares. */
  const appendStage = useCallback(
    (terminal: boolean) => {
      const lastStageId = model?.stages.length ? model.stages[model.stages.length - 1].id : null;
      logAnalyticsEvent('arc_editor_block_added', undefined, { block: 'stage', via: 'button' });
      addStage(lastStageId, t('studio.arcEditor.palette.newStageName'), { terminal });
    },
    [model, addStage, t, logAnalyticsEvent]
  );

  /** The lane grid's trailing "Add stage" column button — a plain stage, same event as the
   * palette's stage tiles (`handleTileActivate`). */
  const handleAddStage = useCallback(() => appendStage(false), [appendStage]);

  const handleAddIntent = useCallback(
    (stageId: string, laneKey: string) => {
      logAnalyticsEvent('arc_editor_block_added', undefined, { block: 'intent', via: 'button' });
      addIntent(stageId, laneKey, t('studio.arcEditor.palette.newIntentName'));
    },
    [addIntent, t, logAnalyticsEvent]
  );

  const handleAddAction = useCallback(
    (stageId: string) => {
      logAnalyticsEvent('arc_editor_block_added', undefined, { block: 'action', via: 'button' });
      addAction(stageId, 'attach_aspect');
    },
    [addAction, logAnalyticsEvent]
  );

  /** Keyboard "Place in…" targets for one palette tile — a stage for an action, a stage × lane
   * for an intent, and none for a stage tile (which has no placement choice; see
   * `handleTileActivate`). */
  const getPlacementTargets = useCallback(
    (item: BlockPaletteItem): BlockPlacementTarget[] => {
      if (!model) return [];
      const paletteItem = ARC_PALETTE_ITEMS.find((i) => i.id === item.id);
      if (!paletteItem || paletteItem.group === 'groupStages') return [];
      if (paletteItem.group === 'groupActions') {
        return model.stages.map((stage) => ({
          value: stage.id,
          label: t('studio.arcEditor.palette.placeInStage', { stage: stage.fallbackName }),
        }));
      }
      const lanes = distinctIntentLanes(model);
      const targets: BlockPlacementTarget[] = [];
      model.stages.forEach((stage) => {
        lanes.forEach((lane) => {
          targets.push({
            value: `${stage.id}::${lane}`,
            label: t('studio.arcEditor.palette.placeInStageLane', { stage: stage.fallbackName, lane }),
          });
        });
      });
      return targets;
    },
    [model, t]
  );

  const handleTilePlace = useCallback(
    (item: BlockPaletteItem, target: BlockPlacementTarget) => {
      const paletteItem = ARC_PALETTE_ITEMS.find((i) => i.id === item.id);
      if (!paletteItem) return;
      logAnalyticsEvent('arc_editor_block_added', undefined, {
        block: ANALYTICS_BLOCK_OF_GROUP[paletteItem.group],
        via: 'keyboard',
      });
      if (paletteItem.group === 'groupActions') {
        addAction(target.value, paletteItem.actionType);
      } else if (paletteItem.group === 'groupIntents') {
        const [stageId, laneKey] = target.value.split('::');
        addIntent(stageId, laneKey, t(`studio.arcEditor.palette.library.${paletteItem.nameKey}`));
      }
    },
    [addAction, addIntent, t, logAnalyticsEvent]
  );

  const handleTileActivate = useCallback(
    (item: BlockPaletteItem) => {
      const paletteItem = ARC_PALETTE_ITEMS.find((i) => i.id === item.id);
      if (paletteItem?.group === 'groupStages') {
        appendStage(paletteItem.id === 'stage-terminal');
      }
    },
    [appendStage]
  );

  const handleViewChange = useCallback(
    (next: ArcEditorCanvasView) => {
      if (next !== view) {
        logAnalyticsEvent('arc_editor_view_changed', undefined, { view: next });
      }
      setView(next);
    },
    [view, logAnalyticsEvent]
  );

  const from = (location.state as { from?: string } | null)?.from;
  const backTarget = from ?? '/studio/arc';
  const backHref = useHref(backTarget);
  const handleBack = useCallback(() => {
    const target = backTarget;
    // The HashRouter here isn't a data router, so `useBlocker` isn't available (same
    // constraint `ContextEditorPage.tsx` documents) — the explicit Back affordance is guarded
    // instead, matching `handleBackToLibrary`'s pattern.
    if (!dirty) {
      navigate(target);
      return;
    }
    confirmDialogAtom({
      header: t('studio.arcEditor.unsavedGuard.title'),
      message: t('studio.arcEditor.unsavedGuard.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('studio.arcEditor.unsavedGuard.leave'),
      rejectLabel: t('studio.arcEditor.unsavedGuard.stay'),
      variant: 'danger',
      accept: () => navigate(target),
    });
  }, [navigate, backTarget, dirty, t]);

  const arcName = status === 'new' ? t('studio.arcEditor.newArcTitle') : row?.name ?? t('studio.arcEditor.loadingTitle');

  const surfaceTag: ArcEditorCanvasSurfaceTag = useMemo(() => {
    if (status === 'new') {
      return { tone: 'warn', glyph: 'pencil', label: t('studio.arcEditor.top.surfaceTagNew') };
    }
    if (row?.is_published) {
      return {
        tone: 'warn',
        glyph: 'pencil',
        label: t('studio.arcEditor.top.surfaceTagPublished', { version: row.version }),
      };
    }
    return {
      tone: 'warn',
      glyph: 'pencil',
      label: t('studio.arcEditor.top.surfaceTagDraft', { version: row?.version ?? 1 }),
    };
  }, [status, row, t]);

  const laneGridStages = useMemo(() => (model?.stages ?? []).map((stage) => ({ id: stage.id })), [model]);
  const intentLaneCounts = useMemo(() => (model ? intentCountByLane(model) : {}), [model]);
  const laneDefinitions = useMemo(
    () =>
      model
        ? resolveLanesForModel(
            model,
            orgAvailableSchemas as unknown as Schema[],
            row?.applicability as UnifiedFilters | undefined
          )
        : [],
    [model, orgAvailableSchemas, row?.applicability]
  );
  const laneGridLanes = useMemo(
    () => laneDefinitions.map((lane) => ({ id: lane.laneKey, pending: lane.pending })),
    [laneDefinitions]
  );
  const unattributedCount = useMemo(() => (model ? unattributedIntentCount(model) : 0), [model]);

  const flowViewNodes: ArcFlowViewNode[] = useMemo(
    () =>
      (model?.stages ?? []).map((stage) => ({
        id: stage.id,
        name: stage.fallbackName,
        isTerminal: stage.isTerminal,
        keyDate: { text: '', vacant: true },
      })),
    [model]
  );

  /** One flow edge per derived `ArcEdge`; the id names the source stage and the
   * `AvailableAction` the edge is drawn from (a calculated exit has none), so an edge click and
   * the transition selection resolve to the same edge. */
  const flowViewTransitions: ArcFlowViewTransition[] = useMemo(
    () =>
      model
        ? deriveEdges(model).map((edge) => ({
            id: flowEdgeId(edge.from, edge.actionId ?? null),
            from: edge.from,
            to: edge.to,
            kind: edge.kind,
            glyph: TRANSITION_KIND_GLYPH[edge.kind],
            label: edge.label,
          }))
        : [],
    [model]
  );

  const flowViewSelection = useMemo(() => {
    if (selectedStageId) return { kind: 'stage' as const, id: selectedStageId };
    if (selectedTransition) {
      return { kind: 'transition' as const, id: flowEdgeId(selectedTransition.stageId, selectedTransition.actionId) };
    }
    return null;
  }, [selectedStageId, selectedTransition]);

  /** An edge click selects the source stage's transition at that edge's action (spec § Model
   * mapping "selecting an edge selects the source stage's transition"). */
  const handleSelectFlowTransition = useCallback(
    (transition: ArcFlowViewTransition) => {
      const edge = model
        ? deriveEdges(model).find((e) => flowEdgeId(e.from, e.actionId ?? null) === transition.id)
        : undefined;
      selectTransition(transition.from, edge?.actionId ?? null);
    },
    [model, selectTransition]
  );

  /** The flow view's "Transition" button (`canvas.transition`) adds a manual-exit target to
   * whichever stage is currently selected, falling back to the first stage when nothing is
   * selected (mirrors the prototype's `addTransition`, which always has a stage to point from). */
  const handleAddTransition = useCallback(() => {
    const stageId = selectedStageId ?? model?.stages[0]?.id;
    if (!stageId) return;
    addTransitionTarget(stageId);
  }, [selectedStageId, model, addTransitionTarget]);

  /** A starter is the one place several stages land at once; creation itself is always empty
   * (D23), so this is where the chosen shape is reported (spec § Analytics, D39). */
  const handleSelectStarter = useCallback(
    (starterId: string) => {
      logAnalyticsEvent('arc_editor_block_added', undefined, { block: 'stage', via: 'starter', starter: starterId });
      addStarterStages(starterId);
    },
    [addStarterStages, logAnalyticsEvent]
  );

  /** `newArc` (`canvas :: demo=new & demo+staged=other`) — offered whenever a loaded template
   * has no stages yet (D23: the row already exists by the time the canvas opens it — the
   * gallery dialog's immediate `POST` — so this is never gated on `status === 'new'`). */
  const newArc = useMemo(() => {
    if (!model || model.stages.length > 0) return undefined;
    return {
      starters: ARC_CANVAS_STARTERS.map((starter) => ({
        id: starter.id,
        label: t(`studio.arcEditor.canvas.newArc.starters.${starter.id}`),
        stageCount: starter.stageNames.length,
      })),
      onSelectStarter: handleSelectStarter,
      onAddSingleStage: handleAddStage,
    };
  }, [model, t, handleSelectStarter, handleAddStage]);

  const intentSelection = useMemo(() => {
    if (!selectedIntent || !model) return undefined;
    const stage = model.stages.find((s) => s.id === selectedIntent.stageId);
    const intent = stage?.intents?.find((i) => i.id === selectedIntent.intentId);
    if (!stage || !intent) return undefined;

    const stageOptions = model.stages.map((s) => ({ value: s.id, label: s.fallbackName }));
    const perspectives = intent.perspectives ?? [];

    return {
      title: intent.name,
      onClose: () => selectStage(null),
      body: {
        stageName: stage.fallbackName,
        whoLabel: perspectives.length
          ? perspectives.join(', ')
          : t('studio.arcEditor.inspector.intent.nobody'),
        name: intent.name,
        onNameChange: (value: string) => updateIntent({ name: value }),
        stageId: stage.id,
        onStageChange: (value: string) => updateIntent({ stageId: value }),
        stageOptions,
        priority: intent.priority,
        onPriorityChange: (priority: StageIntent['priority']) => updateIntent({ priority }),
        perspectives,
        onPerspectivesChange: (value: string[]) => updateIntent({ perspectives: value }),
        perspectiveGroups: [],
        offBaseSchemaLabels: [],
        fulfillmentCondition: intent.fulfillmentCondition,
        onFulfillmentConditionChange: (group: FilterGroup) => updateIntent({ fulfillmentCondition: group }),
        prerequisites: intent.prerequisites,
        onPrerequisitesChange: (group: FilterGroup) => updateIntent({ prerequisites: group }),
        conditionAvailableProperties: [],
        editable,
        onApply: () => selectStage(null),
        onRemove: removeIntent,
      },
    };
  }, [selectedIntent, model, editable, t, updateIntent, removeIntent, selectStage]);

  const actionSelection = useMemo(() => {
    if (!selectedAction || !model) return undefined;
    const stage = model.stages.find((s) => s.id === selectedAction.stageId);
    if (!stage) return undefined;
    const inEnter = (stage.onEnter ?? []).find((a) => a.id === selectedAction.actionId);
    const action = inEnter ?? (stage.onExit ?? []).find((a) => a.id === selectedAction.actionId);
    if (!action) return undefined;

    const hook: 'onEnter' | 'onExit' = inEnter ? 'onEnter' : 'onExit';
    const tier = actionTierOf(action);
    const ns = 'studio.arcEditor.inspector.action';
    const verbKey = ACTION_TYPE_VERB_KEY[action.type];
    const verb = verbKey ? t(verbKey) : action.type;
    const subject = referenceLabel(actionSubjectOf(action)) ?? t('studio.arcEditor.actionCard.noSubject');
    const hookWord = t(`${ns}.hook.${hook === 'onEnter' ? 'onEnterLabel' : 'onExitLabel'}`);
    const tail = actionTierHoldsForPerson(tier)
      ? t(`${ns}.tier.${tier}Label`).toLowerCase()
      : t(`${ns}.sentenceTailRunsOnOwn`);

    // The type select offers the four authored kinds; a factor kind is listed only while it is
    // the current one, so the select reads what the action is without offering to author a
    // factor (configured outside the canvas — spec Edge Cases).
    const authored = isAuthoredActionType(action.type);
    const actionTypeOptions = [
      ...(authored ? [] : [{ value: action.type, label: t(`${ns}.typeLabels.${action.type}`, { defaultValue: action.type }) }]),
      ...AUTHORED_ACTION_TYPES.map((type) => ({ value: type, label: t(`${ns}.typeLabels.${type}`) })),
    ];
    const refOptions = !authored ? null : action.type === 'deploy_approach' ? approachOptions : aspectOptions;
    const refValue = authored ? actionSubjectOf(action) ?? '' : '';
    const target = (action as { target?: TargetResolver }).target;
    const targetKind = target?.kind ?? 'self';
    // "Runs on" authors all five `TargetResolver` kinds the engine executes (spec D8/D39).
    const targetKindOptions = AUTHORED_TARGET_KINDS.map((kind) => ({
      value: kind,
      label: t(`${ns}.targetKinds.${kind}.label`),
    }));
    const targetFilters = target?.kind === 'descendant' ? unifiedToFilterState(target.filters) : undefined;
    const targetSelection =
      target?.kind === 'descendant' || target?.kind === 'context_member' ? target.selection : 'first';
    const targetMaxDepth = target?.kind === 'descendant' ? target.maxDepth : undefined;
    const targetContextSystemName = target?.kind === 'context_member' ? target.contextSystemName : '';
    const targetActionId = target?.kind === 'created_by_action' ? target.actionId : '';
    const targetActionOptions = earlierActionsInHook(stage, hook, action.id).map((earlier) => {
      const earlierVerbKey = ACTION_TYPE_VERB_KEY[earlier.type];
      const earlierVerb = earlierVerbKey ? t(earlierVerbKey) : earlier.type;
      const earlierSubject = referenceLabel(actionSubjectOf(earlier)) ?? t('studio.arcEditor.actionCard.noSubject');
      return { value: earlier.id, label: `${earlierVerb} — ${earlierSubject}` };
    });

    return {
      title: verb,
      onClose: () => selectStage(null),
      body: {
        sentence: t(`${ns}.sentence`, {
          hook: hookWord.charAt(0).toUpperCase() + hookWord.slice(1),
          stageName: stage.fallbackName,
          verb: verb.toLowerCase(),
          subject,
          tail: ` — ${tail}`,
        }),
        actionType: action.type,
        onActionTypeChange: (type: ArcCanvasAuthoredActionType) => updateAction({ type }),
        actionTypeOptions,
        typeNote: authored ? t(`${ns}.typeNotes.${action.type}`) : t(`${ns}.typeNotes.factorOutsideCanvas`),
        refLabel: t(action.type === 'deploy_approach' ? `${ns}.whichApproachLabel` : `${ns}.whichAspectLabel`),
        refValue,
        refOptions,
        onRefChange: (reference: string) => updateAction({ reference }),
        refErrorMessage: referenceCatalogError ? t(`${ns}.refCatalogError`) : undefined,
        refChip: null,
        stageId: stage.id,
        stageOptions: model.stages.map((s) => ({ value: s.id, label: s.fallbackName })),
        onStageChange: (stageId: string) => {
          if (stageId === stage.id) return;
          updateAction({ stageId });
          selectAction(stageId, action.id);
        },
        targetKind,
        targetKindOptions,
        onTargetKindChange: (kind: string) => {
          if (isAuthoredTargetKind(kind)) updateAction({ targetKind: kind });
        },
        targetNote: t(`${ns}.targetKinds.${targetKind}.note`, { defaultValue: '' }),
        targetFilters,
        onTargetFiltersChange: (filters: FilterState) => updateAction({ targetFilters: filterStateToUnified(filters) }),
        targetFiltersAvailableProperties: availableProperties,
        targetSelection,
        onTargetSelectionChange: (value: 'first' | 'all') => updateAction({ targetSelection: value }),
        targetMaxDepth,
        onTargetMaxDepthChange: (value: number | undefined) => updateAction({ targetMaxDepth: value }),
        targetContextSystemName,
        onTargetContextSystemNameChange: (value: string) => updateAction({ targetContextSystemName: value }),
        targetContextOptions: contextOptions,
        targetActionId,
        onTargetActionIdChange: (value: string) => updateAction({ targetActionId: value }),
        targetActionOptions,
        hook,
        onHookChange: (nextHook: 'onEnter' | 'onExit') => updateAction({ hook: nextHook }),
        tier,
        onTierChange: (nextTier: typeof tier) => updateAction({ tier: nextTier }),
        editable,
        onApply: () => selectStage(null),
        onRemove: removeAction,
      },
    };
  }, [
    selectedAction,
    model,
    editable,
    t,
    updateAction,
    removeAction,
    selectStage,
    selectAction,
    aspectOptions,
    approachOptions,
    referenceCatalogError,
    referenceLabel,
    availableProperties,
    contextOptions,
  ]);

  const transitionSelection = useMemo(() => {
    if (!selectedTransition || !model) return undefined;
    const stage = model.stages.find((s) => s.id === selectedTransition.stageId);
    if (!stage) return undefined;

    const ns = 'studio.arcEditor.inspector.transition';
    const transition: TransitionDefinition = stage.transition ?? { type: 'manual' };
    const actions = transition.availableActions ?? [];
    const action =
      selectedTransition.actionId === null ? actions[0] : actions.find((a) => a.id === selectedTransition.actionId);
    const kind = transition.type;
    const toStageId = kind === 'calculated' ? transition.calculatedTarget ?? '' : action?.nextStageId ?? '';
    const toStage = model.stages.find((s) => s.id === toStageId);
    const anchor = transition.dateOffsetAnchor ?? '';
    const anchorSource = transition.anchorSource ?? 'keyDate';
    const offset = transition.dateOffset ?? '';
    const stageOptions = model.stages.map((s) => ({
      value: s.id,
      label: s.isTerminal ? t(`${ns}.stageOptionFinal`, { name: s.fallbackName }) : s.fallbackName,
    }));
    const keyDateOptions = Object.keys(model.keyDates).map((key) => ({ value: key, label: key }));
    const sphereDateOptions = availableProperties
      .filter((property) => property.type === 'date' || property.type === 'datetime')
      .map((property) => ({ value: property.key, label: property.label }));

    // The read-back sentence (`acTransSentence`): where it goes, then how it moves.
    const head = t(`${ns}.summaryHead`, {
      from: stage.fallbackName,
      to: toStage?.fallbackName ?? t(`${ns}.summaryNoTarget`),
    });
    const anchorLabel =
      anchorSource === 'property'
        ? sphereDateOptions.find((option) => option.value === anchor)?.label
        : keyDateOptions.find((option) => option.value === anchor)?.label;
    const summaryTail = transitionSummaryTail(t, ns, stage.fallbackName, transition, anchorLabel);

    return {
      title: action?.label || t(`${ns}.newTransition`),
      onClose: () => selectStage(null),
      body: {
        summary: `${head} ${summaryTail}`,
        fromStageName: stage.fallbackName,
        toStageId,
        onToStageChange: (value: string) => updateTransition({ toStageId: value }),
        stageOptions,
        label: action?.label ?? '',
        onLabelChange: (value: string) => updateTransition({ label: value }),
        kind,
        onKindChange: (value: TransitionDefinition['type']) => updateTransition({ kind: value }),
        anchorSource,
        onAnchorSourceChange: (source: NonNullable<TransitionDefinition['anchorSource']>) =>
          updateTransition({ anchorSource: source, dateOffsetAnchor: '' }),
        anchor,
        onAnchorChange: (value: string) => updateTransition({ dateOffsetAnchor: value }),
        keyDateOptions,
        sphereDateOptions,
        offset,
        onOffsetChange: (value: string) => updateTransition({ dateOffset: value }),
        offsetSupported: isWholeDayOffset(offset),
        condition: transition.calculatedConditions,
        onConditionChange: (group: FilterGroup) => updateTransition({ calculatedConditions: group }),
        conditionAvailableProperties: availableProperties,
        requireComment: Boolean(transition.requireComment),
        onRequireCommentChange: (checked: boolean) => updateTransition({ requireComment: checked }),
        editable,
        onApply: () => selectStage(null),
        onRemove: removeTransition,
        removeKeepsOtherExits: transitionRemoveKeepsOtherExits(transition, selectedTransition.actionId),
      },
    };
  }, [selectedTransition, model, editable, t, availableProperties, updateTransition, removeTransition, selectStage]);

  const stageSelection = useMemo(() => {
    if (!selectedStageId || !model) return undefined;
    const stage = model.stages.find((s) => s.id === selectedStageId);
    if (!stage) return undefined;

    const keyDateOptions = Object.keys(model.keyDates).map((key) => ({ value: key, label: key }));
    const assignedToValue = stage.assignedTo?.value ?? '';
    const laneValues = new Set(distinctIntentLanes(model));
    if (assignedToValue) laneValues.add(assignedToValue);
    const assignedToOptions = Array.from(laneValues).map((value) => ({ value, label: value }));
    const requirements = (stage.requires ?? []).map((requirement, index) => ({
      id: String(index),
      aspectRef: requirement.aspectSystemName,
      aspectOptions,
      prompt: requirement.prompt ?? '',
    }));
    const intents = (stage.intents ?? []).map((intent) => ({
      id: intent.id,
      name: intent.name,
      perspectiveLabel: intent.perspectives?.length
        ? intent.perspectives.join(', ')
        : t('studio.arcEditor.inspector.intent.nobody'),
    }));
    const actionCount = (stage.onEnter?.length ?? 0) + (stage.onExit?.length ?? 0);
    const inboundTransitionCount = deriveEdges(model).filter(
      (edge) => edge.to === stage.id && edge.from !== stage.id
    ).length;

    return {
      title: stage.fallbackName,
      onClose: () => selectStage(null),
      body: {
        name: stage.fallbackName,
        onNameChange: (value: string) => renameStage(stage.id, value),
        keyDateOptions,
        keyDateValue: stage.startOffsetAnchor ?? '',
        onKeyDateChange: (value: string) => updateStage({ startOffsetAnchor: value || undefined }),
        offset: stage.startOffset ?? '',
        onOffsetChange: (value: string) => updateStage({ startOffset: value }),
        offsetSupported: isWholeDayOffset(stage.startOffset),
        vacant: !stage.startOffsetAnchor,
        whenNeed: stage.startOffsetAnchor ?? '',
        whenText: undefined,
        requirements,
        onAddRequirement: addStageRequirement,
        onRemoveRequirement: (id: string) => removeStageRequirement(Number(id)),
        onRequirementAspectChange: (id: string, value: string) =>
          updateStageRequirement(Number(id), { aspectSystemName: value }),
        onRequirementPromptChange: (id: string, value: string) =>
          updateStageRequirement(Number(id), { prompt: value }),
        pinName: '',
        terminalAllowed: true,
        isTerminal: Boolean(stage.isTerminal),
        onIsTerminalChange: (value: boolean) => updateStage({ isTerminal: value }),
        assignedTo: assignedToValue,
        assignedToOptions,
        onAssignedToChange: (value: string) => updateStage({ assignedTo: value }),
        intents,
        actionCount,
        inboundTransitionCount,
        editable,
        onApply: () => selectStage(null),
        onRemove: removeStage,
      },
    };
  }, [
    selectedStageId,
    model,
    editable,
    t,
    renameStage,
    updateStage,
    removeStage,
    addStageRequirement,
    updateStageRequirement,
    removeStageRequirement,
    selectStage,
    aspectOptions,
  ]);

  const arcSelection = useMemo(() => {
    if (!selectedArc || !model) return undefined;
    const settings = arcSettingsOf(model);
    return {
      title: model.name,
      onClose: () => selectStage(null),
      body: {
        name: model.name,
        onNameChange: (value: string) => updateArcMeta({ name: value }),
        description: model.description ?? '',
        onDescriptionChange: (value: string) => updateArcMeta({ description: value }),
        archetype: model.unauthored.archetype,
        applicability: {
          mode: applicabilityMode,
          families: applicabilityFamilies,
          types: applicabilityTypes,
          filter: applicabilityFilter,
          onModeChange: setApplicabilityMode,
          onFamiliesChange: setApplicabilityFamilies,
          onTypesChange: setApplicabilityTypes,
          onFilterChange: setApplicabilityFilter,
          availableProperties,
          familyTypeSchemas: orgAvailableSchemas,
          sources: orgAvailableSources,
          approachSources: orgApproachSources,
          readOnly: !editable,
        },
        keyDates: keyDateRowsOf(model),
        onAddKeyDate: addKeyDate,
        onKeyDateNameChange: (name: string, value: string) => updateKeyDate(name, { name: value }),
        onKeyDateExpressionChange: (name: string, value: string) => updateKeyDate(name, { expression: value }),
        onRemoveKeyDate: removeKeyDate,
        reopenable: settings.reopenable,
        onReopenableChange: (value: boolean) => updateArcSettings({ reopenable: value }),
        allowManualCancellation: settings.allowManualCancellation,
        onAllowManualCancellationChange: (value: boolean) => updateArcSettings({ allowManualCancellation: value }),
        autoComplete: settings.autoComplete,
        onAutoCompleteChange: (value: boolean) => updateArcSettings({ autoComplete: value }),
        editable,
        onApply: () => selectStage(null),
      },
    };
  }, [
    selectedArc,
    model,
    editable,
    updateArcMeta,
    applicabilityMode,
    applicabilityFamilies,
    applicabilityTypes,
    applicabilityFilter,
    availableProperties,
    orgAvailableSchemas,
    orgAvailableSources,
    orgApproachSources,
    addKeyDate,
    updateKeyDate,
    removeKeyDate,
    updateArcSettings,
    selectStage,
  ]);

  const toIntentRowIntent = useCallback(
    (intent: StageIntent): ArcIntentRowIntent => ({
      id: intent.id,
      name: intent.name,
      priority: intent.priority,
    }),
    []
  );

  const toActionCardAction = useCallback(
    (action: StageActionDefinition, hook: 'enter' | 'exit'): ArcActionCardAction => {
      const tier = actionTierOf(action);
      const verbKey = ACTION_TYPE_VERB_KEY[action.type];
      return {
        id: action.id,
        hook:
          hook === 'enter'
            ? { glyph: 'log-in', word: t('studio.arcEditor.inspector.action.hook.onEnterLabel') }
            : { glyph: 'log-out', word: t('studio.arcEditor.inspector.action.hook.onExitLabel') },
        verb: (verbKey ? t(verbKey) : action.type).toLowerCase(),
        subject: actionSubjectOf(action) ?? t('studio.arcEditor.actionCard.noSubject'),
        tier: {
          label: t(`studio.arcEditor.inspector.action.tier.${tier}Label`).toLowerCase(),
          holdsForPerson: actionTierHoldsForPerson(tier),
        },
      };
    },
    [t]
  );

  const paletteGroups = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    return ARC_PALETTE_GROUP_ORDER.map((groupKey) => {
      const items = ARC_PALETTE_ITEMS.filter(
        (item) => item.group === groupKey && (!q || t(`studio.arcEditor.palette.library.${item.nameKey}`).toLowerCase().includes(q))
      ).map((item) => ({
        id: item.id,
        name: t(`studio.arcEditor.palette.library.${item.nameKey}`),
        glyph: item.glyph,
        note: t(`studio.arcEditor.palette.library.${item.noteKey}`),
      }));
      return { id: groupKey, label: t(`studio.arcEditor.palette.${groupKey}`), items };
    }).filter((group) => group.items.length > 0);
  }, [paletteQuery, t]);

  if (status === 'notFound' || status === 'degraded') {
    // Load failed (S2): what happened, and Retry as the way forward — the row never arrived,
    // so there is no draft to keep and nothing else the author can do here.
    const banner =
      status === 'degraded' ? (
        <MessageAtom
          severity="error"
          iconClass="pi pi-exclamation-triangle"
          data-testid="arc-editor-load-failed-banner"
          text={
            <span className="arc-editor-page__load-failed">
              {t('studio.arcEditor.loadFailed')}
              <ButtonAtom
                variant="text"
                size="small"
                label={t('common.retry')}
                onClick={retry}
                data-testid="arc-editor-load-failed-retry"
              />
            </span>
          }
        />
      ) : undefined;
    return (
      <div className="arc-editor-page" data-testid="arc-editor-page">
        <EditorRailShellOrganism
          locked
          banner={banner}
          header={
            <EditorHeaderMolecule
              data-testid="arc-editor-page-header"
              onBack={handleBack}
              backAriaLabel={t('studio.arcEditor.backToStudio')}
              backTestId="arc-editor-back"
              glyph={<GlyphAtom glyph="lucide:shuffle" aria-hidden />}
              title={arcName}
              below={
                isSystem ? (
                  <TagAtom
                    label={t('studio.arcEditor.systemPill')}
                    tone="neutral"
                    size="small"
                    data-testid="arc-editor-system-pill"
                  />
                ) : undefined
              }
            />
          }
          documentRegion="canvas"
        >
          {status === 'notFound' && (
            <p data-testid="arc-editor-not-found">{t('studio.arcEditor.notFound')}</p>
          )}
        </EditorRailShellOrganism>
      </div>
    );
  }

  return (
    <div className="arc-editor-page" data-testid="arc-editor-page">
      <ArcEditorCanvasOrganism
        arcName={arcName}
        editable={editable}
        onBack={handleBack}
        backHref={backHref}
        backAriaLabel={t('studio.arcEditor.backToStudio')}
        surfaceTag={surfaceTag}
        unsavedCount={dirty ? unsavedChangeCount : undefined}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saving={saving}
        saveError={saveErrorMessage ? { message: saveErrorMessage, onRetry: handleSave } : undefined}
        view={view}
        onViewChange={handleViewChange}
        stagesCount={model?.stages.length ?? 0}
        pathsCount={summaryCounts.pathsCount}
        intentsCount={summaryCounts.intentsCount}
        actionsCount={summaryCounts.actionsCount}
        unreachableCount={summaryCounts.unreachableCount}
        loading={status === 'loading'}
        newArc={newArc}
        palette={{
          query: paletteQuery,
          onQueryChange: setPaletteQuery,
          groups: paletteGroups,
          draggingId: draggingId ?? undefined,
          onTileDragStart: setDraggingId,
          onTileDragEnd: () => setDraggingId(null),
          onAddStage: handleAddStage,
          getPlacementTargets,
          onTilePlace: handleTilePlace,
          onTileActivate: handleTileActivate,
        }}
        laneGrid={{
          stages: laneGridStages,
          lanes: laneGridLanes,
          editable,
          onAddStage: handleAddStage,
          onCellDrop: handleCellDrop,
          onStageActivate: (stage) => selectStage(stage.id),
          onClearSelection: () => selectStage(null),
          renderStageHeader: (stage, index) => {
            const data = model?.stages.find((s) => s.id === stage.id);
            if (!data) return null;
            return (
              <ArcStageCardMolecule
                stage={{ id: data.id, name: data.fallbackName, isTerminal: data.isTerminal }}
                index={index}
                keyDate={{ text: '', vacant: true }}
                editable={editable}
                selected={selectedStageId === data.id}
                onSelect={() => selectStage(data.id)}
                onRename={(name) => renameStage(data.id, name)}
                onSelectTransition={() => selectTransition(data.id)}
                onGoFlow={() => undefined}
                onGoStoryboard={() => undefined}
              />
            );
          },
          renderSystemLaneLabel: () => (
            <ArcLaneLabelMolecule
              glyph="settings-2"
              name={t('studio.arcEditor.grid.systemLaneName')}
              note={t('studio.arcEditor.grid.systemLaneNote')}
              tone="system"
            />
          ),
          renderSystemCell: (stage) => {
            const data = model?.stages.find((s) => s.id === stage.id);
            if (!data) return null;
            const actions: ArcActionCardAction[] = [
              ...(data.onEnter ?? []).map((action) => toActionCardAction(action, 'enter')),
              ...(data.onExit ?? []).map((action) => toActionCardAction(action, 'exit')),
            ];
            return (
              <ArcActionCardMolecule
                actions={actions}
                editable={editable}
                onSelect={(action) => selectAction(data.id, action.id)}
                onAdd={() => handleAddAction(data.id)}
              />
            );
          },
          renderLaneLabel: (lane) => (
            <ArcLaneLabelMolecule
              glyph="user"
              name={lane.id}
              count={intentLaneCounts[lane.id] ?? 0}
              pending={lane.pending}
            />
          ),
          renderLaneCell: (stage, lane) => {
            const data = model?.stages.find((s) => s.id === stage.id);
            if (!data) return null;
            const intents = intentsInLane(data, lane.id).map(toIntentRowIntent);
            return (
              <ArcIntentRowMolecule
                intents={intents}
                editable={editable}
                onSelect={(intent) => selectIntent(data.id, intent.id)}
                onAdd={() => handleAddIntent(data.id, lane.id)}
                onAcceptIntent={() => undefined}
                onDiscardIntent={() => undefined}
              />
            );
          },
          renderUnattributedLabel: () => (
            <ArcLaneLabelMolecule
              glyph="circle-user-round"
              name={t('studio.arcEditor.grid.anyPerspectiveName')}
              note={t('studio.arcEditor.grid.anyPerspectiveNote')}
              count={unattributedCount}
              tone="any"
            />
          ),
          renderUnattributedCell: (stage) => {
            const data = model?.stages.find((s) => s.id === stage.id);
            if (!data) return null;
            const intents = intentsInLane(data, null).map(toIntentRowIntent);
            return (
              <ArcIntentRowMolecule
                intents={intents}
                editable={editable}
                onSelect={(intent) => selectIntent(data.id, intent.id)}
                onAdd={() => handleAddIntent(data.id, UNATTRIBUTED_LANE_KEY)}
                onAcceptIntent={() => undefined}
                onDiscardIntent={() => undefined}
              />
            );
          },
        }}
        flowView={{
          nodes: flowViewNodes,
          transitions: flowViewTransitions,
          selection: flowViewSelection,
          onSelectStage: (node) => selectStage(node.id),
          onSelectTransition: handleSelectFlowTransition,
          onAddTransition: handleAddTransition,
        }}
        stageSelection={stageSelection}
        transitionSelection={transitionSelection}
        intentSelection={intentSelection}
        actionSelection={actionSelection}
        arcSelection={arcSelection}
        onSelectArc={model ? selectArc : undefined}
        footer={
          row && model
            ? {
                gateStatus: 'ready',
                checks: footerChecks,
                locked: !editable,
                onCheckActivate: handleCheckActivate,
              }
            : undefined
        }
        onPublish={row && model && editable ? handlePublishClick : undefined}
        publishing={publishing}
      />
    </div>
  );
};

export default ArcEditorPage;
